import { useState, useEffect, useCallback } from 'react'
import { Plus, Trash2, Lock, Loader2, X, AlertTriangle } from 'lucide-react'
import api from '../../core/services/api'
import { ConfirmModal } from '../Toast'

interface Props {
  commessaId: string
  onToast: (msg: string, type: 'success' | 'error') => void
}

interface Personale {
  id: string
  nome: string
  cognome: string
}

interface Allocazione {
  id: string
  personale_id: string
  data: string
  ore: number
  commessa_id?: string
  fascicolo_id?: string
  attivita?: string
  note?: string
  is_locked: boolean
  // joined
  personale_nome?: string
  commessa_codice?: string
  fascicolo_codice?: string
}

interface Disponibilita {
  ore_da_busta: number
  ore_riservate: number
  ore_allocate: number
  ore_disponibili: number
  dettaglio_allocazioni: { commessa?: string; fascicolo?: string; ore: number }[]
}

interface AllocazioneForm {
  personale_id: string
  data: string
  ore: string
  attivita: string
  note: string
}

const emptyForm: AllocazioneForm = {
  personale_id: '',
  data: '',
  ore: '',
  attivita: '',
  note: '',
}

export default function AllocazioneOreTab({ commessaId, onToast }: Props) {
  const [personaleList, setPersonaleList] = useState<Personale[]>([])
  const [allocazioni, setAllocazioni] = useState<Allocazione[]>([])
  const [disponibilita, setDisponibilita] = useState<Disponibilita | null>(null)
  const [loading, setLoading] = useState(false)

  // Filters
  const [filterPersonale, setFilterPersonale] = useState('')
  const [filterDataDa, setFilterDataDa] = useState('')
  const [filterDataA, setFilterDataA] = useState('')

  // Modal
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<AllocazioneForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState<Allocazione | null>(null)

  useEffect(() => {
    loadBaseData()
  }, [])

  const loadBaseData = async () => {
    try {
      const persRes = await api.get('/v1/personale')
      setPersonaleList(persRes.data)
    } catch {
      onToast('Errore nel caricamento dati base', 'error')
    }
  }

  const loadAllocazioni = useCallback(async () => {
    try {
      setLoading(true)
      const params: Record<string, string> = { commessa_id: commessaId }
      if (filterPersonale) params.personale_id = filterPersonale
      if (filterDataDa) params.data_da = filterDataDa
      if (filterDataA) params.data_a = filterDataA

      const res = await api.get('/v1/ore/allocazioni', { params })
      setAllocazioni(res.data)
    } catch {
      onToast('Errore nel caricamento allocazioni', 'error')
    } finally {
      setLoading(false)
    }
  }, [commessaId, filterPersonale, filterDataDa, filterDataA, onToast])

  useEffect(() => {
    loadAllocazioni()
  }, [loadAllocazioni])

  // Load disponibilita when a personale + date range is selected
  const loadDisponibilita = useCallback(async () => {
    if (!filterPersonale || !filterDataDa || !filterDataA) {
      setDisponibilita(null)
      return
    }
    try {
      const res = await api.get(`/v1/ore/disponibilita/${filterPersonale}`, {
        params: { data_da: filterDataDa, data_a: filterDataA }
      })
      setDisponibilita(res.data)
    } catch {
      // non-critical
    }
  }, [filterPersonale, filterDataDa, filterDataA])

  useEffect(() => {
    loadDisponibilita()
  }, [loadDisponibilita])

  const handleCreate = () => {
    setForm({ ...emptyForm, personale_id: filterPersonale })
    setError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.personale_id) { setError('Seleziona un dipendente'); return }
    if (!form.data) { setError('Seleziona una data'); return }
    if (!form.ore || parseFloat(form.ore) <= 0) { setError('Inserisci ore valide'); return }

    setSaving(true)
    try {
      const payload: Record<string, any> = {
        personale_id: form.personale_id,
        data: form.data,
        ore: parseFloat(form.ore),
        commessa_id: commessaId,
      }
      if (form.attivita.trim()) payload.attivita = form.attivita.trim()
      if (form.note.trim()) payload.note = form.note.trim()

      await api.post('/v1/ore/allocazioni', payload)
      onToast('Allocazione creata', 'success')
      setShowModal(false)
      loadAllocazioni()
      loadDisponibilita()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (alloc: Allocazione) => {
    try {
      await api.delete(`/v1/ore/allocazioni/${alloc.id}`)
      onToast('Allocazione eliminata', 'success')
      setConfirmDelete(null)
      loadAllocazioni()
      loadDisponibilita()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore nell\'eliminazione', 'error')
      setConfirmDelete(null)
    }
  }

  const getPersonaleNome = (id: string) => {
    const p = personaleList.find(x => x.id === id)
    return p ? `${p.cognome} ${p.nome}` : id
  }

  const formatDate = (s: string) => {
    const d = new Date(s + 'T00:00:00')
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  // Disponibilita bar percentages
  const dispTotal = disponibilita?.ore_da_busta || 0
  const dispAllocate = disponibilita?.ore_allocate || 0
  const dispRiservate = disponibilita?.ore_riservate || 0
  const dispDisponibili = disponibilita?.ore_disponibili || 0

  return (
    <div>
      {/* Action bar */}
      <div className="flex items-center justify-end mb-4">
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Aggiungi
        </button>
      </div>

      {/* Disponibilita bar */}
      {disponibilita && dispTotal > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Disponibilita Ore</span>
            <span className="text-xs text-gray-500">
              {dispAllocate + dispRiservate}h / {dispTotal}h utilizzate
            </span>
          </div>
          <div className="w-full h-6 bg-gray-100 rounded-full overflow-hidden flex">
            <div
              className="bg-blue-500 h-full transition-all"
              style={{ width: `${Math.min(100, (dispAllocate / dispTotal) * 100)}%` }}
              title={`Allocate: ${dispAllocate}h`}
            />
            <div
              className="bg-amber-400 h-full transition-all"
              style={{ width: `${Math.min(100 - (dispAllocate / dispTotal) * 100, (dispRiservate / dispTotal) * 100)}%` }}
              title={`Riservate: ${dispRiservate}h`}
            />
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500" /> Allocate: {dispAllocate}h</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" /> Riservate: {dispRiservate}h</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 border" /> Disponibili: {dispDisponibili}h</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Dipendente</label>
            <select
              value={filterPersonale}
              onChange={e => setFilterPersonale(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Tutti</option>
              {personaleList.map(p => (
                <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Da</label>
            <input
              type="date"
              value={filterDataDa}
              onChange={e => setFilterDataDa(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">A</label>
            <input
              type="date"
              value={filterDataA}
              onChange={e => setFilterDataA(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : allocazioni.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p>Nessuna allocazione trovata</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Data</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Dipendente</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Ore</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Attivita</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500">Stato</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allocazioni.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{formatDate(a.data)}</td>
                    <td className="px-4 py-3 text-gray-700">{a.personale_nome || getPersonaleNome(a.personale_id)}</td>
                    <td className="px-4 py-3 font-semibold text-gray-800">{a.ore}h</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{a.attivita || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      {a.is_locked ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded-full" title="Ore gia' rendicontate in un SAL">
                          <Lock className="w-3 h-3" /> Bloccata
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                          Libera
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => a.is_locked ? null : setConfirmDelete(a)}
                        disabled={a.is_locked}
                        className={`p-1.5 rounded transition-colors ${
                          a.is_locked
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-red-500 hover:bg-red-50'
                        }`}
                        title={a.is_locked ? "Ore gia' rendicontate" : 'Elimina'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Nuova Allocazione</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Dipendente *</label>
                <select
                  value={form.personale_id}
                  onChange={e => setForm({ ...form, personale_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Seleziona...</option>
                  {personaleList.map(p => (
                    <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Data *</label>
                  <input
                    type="date"
                    value={form.data}
                    onChange={e => setForm({ ...form, data: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Ore *</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={form.ore}
                    onChange={e => setForm({ ...form, ore: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="8"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Attivita</label>
                <input
                  type="text"
                  value={form.attivita}
                  onChange={e => setForm({ ...form, attivita: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Descrizione attivita..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Note</label>
                <textarea
                  value={form.note}
                  onChange={e => setForm({ ...form, note: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Salva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <ConfirmModal
          isOpen={true}
          title="Elimina Allocazione"
          message={`Sei sicuro di voler eliminare l'allocazione di ${confirmDelete.ore}h del ${formatDate(confirmDelete.data)}?`}
          confirmText="Elimina"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
          variant="danger"
        />
      )}
    </div>
  )
}
